import { useCallback, useEffect, useMemo, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import parkingYardService from "../../../services/parkingYard.service";
import bankService from "../../../services/bank.service";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import ParkingYardUploadModal from "./ParkingYardUploadModal";
import DownloadSampleExcelButton from "../../../components/common/DownloadSampleExcelButton";
import { saveExcelBlob, getApiErrorMessage } from "../../../utils/downloadExcel";
import "../../../styles/users.css";
import "../../../styles/bankDetails.css";
import "../../../styles/confirmDialog.css";
import "../../../styles/parkingYardAdmin.css";

function sessionKey(bankName, branchName) {
  return `${(bankName || "").trim().toLowerCase()}|||${(branchName || "").trim().toLowerCase()}`;
}

function storageKey(userId) {
  return `fr_parking_yard_upload_sessions_${userId || "anon"}`;
}

function formatUploadDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function isNamedSession(row) {
  return Boolean(String(row?.bankName || "").trim() && String(row?.branchName || "").trim());
}

function readSessions(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    const rows = Array.isArray(parsed) ? parsed.filter(isNamedSession) : [];
    if (Array.isArray(parsed) && rows.length !== parsed.length) {
      writeSessions(userId, rows);
    }
    return rows;
  } catch {
    return [];
  }
}

function writeSessions(userId, sessions) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(sessions));
  } catch {
    /* ignore quota / private mode */
  }
}

function statusLabel(status) {
  if (status === "completed") return "Completed";
  if (status === "processing") return "Processing";
  if (status === "failed") return "Failed";
  return "Upload";
}

function statusClass(status) {
  if (status === "completed") return "bd-status-badge bd-status-badge--uploaded";
  if (status === "processing") return "bd-status-badge py-status-badge--processing";
  if (status === "failed") return "bd-status-badge py-status-badge--failed";
  return "bd-status-badge bd-status-badge--upload";
}

function uploaderDisplayName(uploadedBy, fallbackName = "") {
  if (uploadedBy && typeof uploadedBy === "object") {
    return String(uploadedBy.name || uploadedBy.email || "").trim();
  }
  return String(uploadedBy || fallbackName || "").trim();
}

export default function SsdiUploadParkingYard() {
  const { auth } = useAuth();
  const userId = auth?.user?.id || auth?.user?._id || "";
  const role = auth?.user?.role || "";
  const isBankUser = role === "BANK_ADMIN" || role === "BANK_PERSON";
  const isSsdi = role === "SSDI_SUPER_ADMIN";
  const lockedBankName = String(auth?.user?.bank?.bankName || "").trim();

  const [banks, setBanks] = useState([]);
  const [bankName, setBankName] = useState(isBankUser ? lockedBankName : "");
  const [branchName, setBranchName] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [formMessage, setFormMessage] = useState("");

  const [sessions, setSessions] = useState([]);
  const [uploadSources, setUploadSources] = useState([]);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setSessions(readSessions(userId));
  }, [userId]);

  useEffect(() => {
    if (isBankUser && lockedBankName) {
      setBankName(lockedBankName);
    }
  }, [isBankUser, lockedBankName]);

  const loadBankOptions = useCallback(async () => {
    if (!isSsdi) return;
    try {
      const res = await bankService.ssdiListBanks({ page: 1, limit: 500 });
      const items = res?.data?.data?.banks || [];
      setBanks(Array.isArray(items) ? items : []);
    } catch {
      setBanks([]);
    }
  }, [isSsdi]);

  useEffect(() => {
    loadBankOptions();
  }, [loadBankOptions]);

  const loadUploadSources = useCallback(async () => {
    try {
      const res = await parkingYardService.listUploadSources();
      const items = res?.data?.data?.sources || res?.data?.sources || [];
      setUploadSources(Array.isArray(items) ? items : []);
    } catch {
      setUploadSources([]);
    }
  }, []);

  useEffect(() => {
    loadUploadSources();
  }, [loadUploadSources]);

  const tableRows = useMemo(() => {
    const map = new Map();
    sessions.forEach((row) => {
      map.set(sessionKey(row.bankName, row.branchName), { ...row });
    });
    const sourceKeys = new Set();
    uploadSources.forEach((source) => {
      const key = sessionKey(source.bankName, source.branchName);
      if (!key || key === "|||") return;
      sourceKeys.add(key);
      const prev = map.get(key) || {
        bankName: source.bankName,
        branchName: source.branchName,
        createdAt: source.updatedAt,
      };
      map.set(key, {
        ...prev,
        bankName: source.bankName || prev.bankName,
        branchName: source.branchName || prev.branchName,
        status: prev.status === "processing" ? "processing" : "completed",
        completedAt: source.updatedAt || prev.completedAt,
        totalRecords: source.totalRecords,
        uploadedBy: source.uploadedBy || prev.uploadedBy,
        uploadedByName: uploaderDisplayName(source.uploadedBy, prev.uploadedByName),
      });
    });
    for (const [key, row] of map.entries()) {
      if (sourceKeys.has(key) || row.status === "processing") continue;
      if (row.status === "completed") {
        map.set(key, {
          ...row,
          status: "pending",
          completedAt: null,
          totalRecords: null,
          uploadedBy: null,
          uploadedByName: "",
        });
      }
    }
    return Array.from(map.values()).filter(isNamedSession);
  }, [sessions, uploadSources]);

  const upsertSession = (bank, branch, patch) => {
    const key = sessionKey(bank, branch);
    const now = new Date().toISOString();
    setSessions((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const index = next.findIndex((row) => sessionKey(row.bankName, row.branchName) === key);
      if (index === -1) {
        next.unshift({
          bankName: bank,
          branchName: branch,
          status: "pending",
          createdAt: now,
          updatedAt: now,
          totalRecords: null,
          ...patch,
        });
      } else {
        next[index] = {
          ...next[index],
          ...patch,
          updatedAt: now,
        };
      }
      writeSessions(userId, next);
      return next;
    });
  };

  const branchOptions = useMemo(() => {
    const names = new Set();
    const bankHint = String(auth?.user?.bank?.branchName || auth?.user?.branchName || "").trim();
    if (bankHint) names.add(bankHint);
    sessions.forEach((row) => {
      if (
        String(row.bankName || "").trim().toLowerCase() ===
        String(bankName || "").trim().toLowerCase()
      ) {
        if (row.branchName) names.add(row.branchName);
      }
    });
    return Array.from(names);
  }, [auth?.user?.bank?.branchName, auth?.user?.branchName, bankName, sessions]);

  const handleSaveBank = (e) => {
    e.preventDefault();
    setFormError("");
    setFormMessage("");

    const trimmedBank = bankName.trim();
    const trimmedBranch = branchName.trim();

    if (!trimmedBank) {
      setFormError("Bank name is required.");
      return;
    }
    if (!trimmedBranch) {
      setFormError("Branch name is required.");
      return;
    }

    setSaving(true);
    try {
      const exists = sessions.some(
        (row) => sessionKey(row.bankName, row.branchName) === sessionKey(trimmedBank, trimmedBranch)
      );
      if (!exists) {
        upsertSession(trimmedBank, trimmedBranch, { status: "pending" });
      }
      setFormMessage("Bank details saved. Click Upload in the table to add parking-yard Excel data.");
      setBankName(isBankUser ? lockedBankName : "");
      setBranchName("");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setBankName(isBankUser ? lockedBankName : "");
    setBranchName("");
    setFormError("");
    setFormMessage("");
  };

  const handleSample = async () => {
    try {
      const response = await parkingYardService.downloadSample();
      await saveExcelBlob(response, "parking-yard-sample.xlsx");
    } catch (err) {
      setFormError(await getApiErrorMessage(err, "Failed to download sample Excel."));
    }
  };

  const openUploadForRow = (row) => {
    if (row.status === "processing") return;
    setFormError("");
    setUploadTarget({ bankName: row.bankName, branchName: row.branchName });
  };

  const handleUploadSuccess = (stats) => {
    if (!uploadTarget) return;
    const uploadedByName =
      uploaderDisplayName(stats?.uploadedBy, auth?.user?.name || auth?.user?.email);
    upsertSession(uploadTarget.bankName, uploadTarget.branchName, {
      status: "completed",
      completedAt: new Date().toISOString(),
      totalRecords: stats?.totalRecords,
      inserted: stats?.inserted,
      updated: stats?.updated,
      skipped: stats?.skipped,
      errors: stats?.errors,
      uploadedBy: stats?.uploadedBy || {
        id: userId,
        name: auth?.user?.name || "",
        email: auth?.user?.email || "",
        role,
      },
      uploadedByName,
    });
    setFormMessage(
      `Upload complete for ${uploadTarget.bankName} – ${uploadTarget.branchName}.`
    );
    loadUploadSources();
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setFormError("");
    try {
      if (isNamedSession(deleteTarget)) {
        await parkingYardService.removeBySource({
          bankName: deleteTarget.bankName,
          branchName: deleteTarget.branchName,
        });
      }
      const key = sessionKey(deleteTarget.bankName, deleteTarget.branchName);
      setSessions((prev) => {
        const next = (prev || []).filter(
          (row) => sessionKey(row.bankName, row.branchName) !== key
        );
        writeSessions(userId, next);
        return next;
      });
      if (
        uploadTarget &&
        sessionKey(uploadTarget.bankName, uploadTarget.branchName) === key
      ) {
        setUploadTarget(null);
      }
      setFormMessage(
        `Upload data removed for ${deleteTarget.bankName} – ${deleteTarget.branchName}.`
      );
      setDeleteTarget(null);
      loadUploadSources();
    } catch (err) {
      setFormError(
        await getApiErrorMessage(err, "Failed to delete parking yard upload data.")
      );
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bd-page py-admin">
      <div className="bd-header">
        <h1>Upload Parking Yard</h1>
        <p>
          Add bank and branch details first. After saving, click Upload in the table to add
          parking-yard Excel data for that bank and branch.
        </p>
        <DownloadSampleExcelButton
          showFieldSelect={false}
          className="bd-sample-excel-btn"
          label="Download sample Excel"
          onDownload={handleSample}
        />
      </div>

      <div className="bd-form-card">
        <form className="bd-form-grid" onSubmit={handleSaveBank}>
          <label className="bd-field">
            <span className="bd-label">Bank Name *</span>
            <input
              list="py-bank-name-options"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Select Bank"
              disabled={saving}
            />
            <datalist id="py-bank-name-options">
              {banks.map((bank) => (
                <option key={bank._id || bank.bankName} value={bank.bankName} />
              ))}
            </datalist>
          </label>

          <label className="bd-field">
            <span className="bd-label">Branch Name *</span>
            <input
              list="py-branch-name-options"
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              placeholder="Select Branch"
              disabled={saving}
            />
            <datalist id="py-branch-name-options">
              {branchOptions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </label>

          {formError ? <p className="bd-feedback bd-error">{formError}</p> : null}
          {formMessage ? <p className="bd-feedback bd-success">{formMessage}</p> : null}

          <div className="bd-form-actions">
            <button
              type="button"
              className="bd-btn bd-btn-secondary"
              disabled={saving}
              onClick={handleClear}
            >
              Clear
            </button>
            <button type="submit" className="bd-btn bd-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save Bank Details"}
            </button>
          </div>
        </form>
      </div>

      <div className="bd-table-card">
        <h2>Saved banks &amp; branches</h2>
        {tableRows.length === 0 ? (
          <p className="bd-muted">
            No banks yet. Enter bank name and branch name above, then save.
          </p>
        ) : (
          <div className="company-table-wrap bd-excel-sheet">
            <table className="users-table excel-grid-table bd-excel-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Bank Name</th>
                  <th>Branch Name</th>
                  <th>Uploaded by</th>
                  <th>Status</th>
                  <th>Date &amp; Time</th>
                  <th>Total records</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, index) => {
                  const isComplete = row.status === "completed";
                  const uploadedAt = formatUploadDateTime(row.completedAt || row.updatedAt);
                  const total =
                    row.totalRecords != null && !Number.isNaN(Number(row.totalRecords))
                      ? Number(row.totalRecords)
                      : null;
                  const uploadedByName = uploaderDisplayName(row.uploadedBy, row.uploadedByName);

                  return (
                    <tr key={sessionKey(row.bankName, row.branchName)}>
                      <td>{index + 1}</td>
                      <td>{row.bankName}</td>
                      <td>{row.branchName}</td>
                      <td>
                        {isComplete && uploadedByName ? uploadedByName : (
                          <span className="bd-upload-meta bd-upload-meta--pending">—</span>
                        )}
                      </td>
                      <td className="bd-col-status">
                        <button
                          type="button"
                          className={statusClass(row.status)}
                          disabled={row.status === "processing"}
                          onClick={() => openUploadForRow(row)}
                        >
                          {statusLabel(row.status)}
                        </button>
                      </td>
                      <td className="bd-col-datetime">
                        {isComplete && uploadedAt ? (
                          <span className="bd-upload-meta">{uploadedAt}</span>
                        ) : (
                          <span className="bd-upload-meta bd-upload-meta--pending">—</span>
                        )}
                      </td>
                      <td className="bd-col-rows">
                        {isComplete && total != null ? (
                          <span className="bd-upload-meta">
                            {total.toLocaleString("en-IN")}
                          </span>
                        ) : (
                          <span className="bd-upload-meta bd-upload-meta--pending">—</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="secondary-page-btn py-delete"
                          disabled={row.status === "processing" || deleting}
                          onClick={() => setDeleteTarget(row)}
                        >
                          <FiTrash2 /> Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ParkingYardUploadModal
        open={Boolean(uploadTarget)}
        bankName={uploadTarget?.bankName || ""}
        branchName={uploadTarget?.branchName || ""}
        onClose={() => setUploadTarget(null)}
        onStatusChange={(status) => {
          if (!uploadTarget) return;
          upsertSession(uploadTarget.bankName, uploadTarget.branchName, { status });
        }}
        onSuccess={handleUploadSuccess}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete parking yard upload?"
        message={
          deleteTarget
            ? `This will delete parking-yard Excel data for ${deleteTarget.bankName} – ${deleteTarget.branchName}. Other bank and branch records will not be changed.`
            : ""
        }
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
        onCancel={() => {
          if (deleting) return;
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
